import { useState } from "react";
import axios from "axios";
import { useTheme } from "@/components/ThemeContext";
import SEOHead from "@/components/SEOHead";
import { ContattiStructuredData } from "@/components/StructuredData";
import { Phone, Mail, MapPin, MessageCircle, Send, Clock, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ScrollReveal from "@/components/ScrollReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContattiPage() {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    messaggio: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { isDark } = useTheme();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.email || !formData.messaggio) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Messaggio inviato con successo!");
      setFormData({ nome: "", email: "", telefono: "", messaggio: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o contattaci telefonicamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contatti-page" className="pt-20">
      <SEOHead
        title="Contatti - Telefono, Email, WhatsApp"
        description="Contatta Transfer Avellino: telefono +39 378 4035054, email info@transferavellino.com, WhatsApp. Disponibili H24 per prenotazioni NCC, preventivi e informazioni. Sede ad Avellino, Campania."
        path="/contatti"
      />
      <ContattiStructuredData />
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left */}
            <div className="lg:col-span-5">
              <ScrollReveal>
                <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-6">
                  Contatti
                </span>
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl tracking-tight font-normal leading-[1.1] mb-8 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                  Parliamo del vostro prossimo
                  <span className="italic text-[#C9A646]"> viaggio</span>
                </h1>
              </ScrollReveal>

              <ScrollReveal delay={0.1}>
                <div className="space-y-6 mb-10">
                  <a
                    href="tel:+393784035054"
                    data-testid="contact-phone"
                    className="flex items-center gap-4 group"
                  >
                    <div className={`w-12 h-12 border border-[#C9A646]/30 flex items-center justify-center group-hover:border-[#C9A646] group-hover:bg-[#C9A646]/10 transition-all duration-300`}>
                      <Phone className="w-5 h-5 text-[#C9A646]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>Telefono</p>
                      <p className={`font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>+39 378 4035054</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@transferavellino.com"
                    data-testid="contact-email"
                    className="flex items-center gap-4 group"
                  >
                    <div className={`w-12 h-12 border border-[#C9A646]/30 flex items-center justify-center group-hover:border-[#C9A646] group-hover:bg-[#C9A646]/10 transition-all duration-300`}>
                      <Mail className="w-5 h-5 text-[#C9A646]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>Email</p>
                      <p className={`font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>info@transferavellino.com</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-[#C9A646]/30 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#C9A646]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>Sede</p>
                      <p className={`font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>Avellino, Italia</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-[#C9A646]/30 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#C9A646]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>Disponibilità</p>
                      <p className={`font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>24 ore su 24 e 7 giorni su 7</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <a
                  href="https://wa.me/393784035054?text=Salve%2C%20vorrei%20informazioni%20sul%20servizio%20NCC."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  Scrivici su WhatsApp
                </a>
              </ScrollReveal>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-7">
              <ScrollReveal delay={0.15}>
                {success ? (
                  <div className={`border p-8 md:p-10 text-center ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}>
                    <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#C9A646] mb-6">
                      <Check className="w-8 h-8 text-[#C9A646]" strokeWidth={1.5} />
                    </div>
                    <h3 className={`text-xl font-medium mb-3 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>Messaggio Inviato</h3>
                    <p className={`text-sm mb-6 ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                      Vi risponderemo nel più breve tempo possibile.
                    </p>
                    <button
                      onClick={() => setSuccess(false)}
                      data-testid="contact-new-msg-btn"
                      className="text-[#C9A646] text-sm hover:underline"
                    >
                      Invia un altro messaggio
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className={`border p-8 md:p-10 ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}
                  >
                    <h3 className={`text-xl font-medium mb-6 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>Inviaci un Messaggio</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label htmlFor="c-nome" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                          Nome e Cognome *
                        </Label>
                        <Input
                          id="c-nome"
                          data-testid="contact-form-nome"
                          value={formData.nome}
                          onChange={(e) => handleChange("nome", e.target.value)}
                          placeholder="Mario Rossi"
                          className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="c-email" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                          Email *
                        </Label>
                        <Input
                          id="c-email"
                          type="email"
                          data-testid="contact-form-email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="mario@email.com"
                          className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <Label htmlFor="c-tel" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Telefono
                      </Label>
                      <Input
                        id="c-tel"
                        type="tel"
                        data-testid="contact-form-telefono"
                        value={formData.telefono}
                        onChange={(e) => handleChange("telefono", e.target.value)}
                        placeholder="+39 333 1234567"
                        className={`border rounded-none h-12 ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                      />
                    </div>

                    <div className="mb-8">
                      <Label htmlFor="c-msg" className={`text-sm mb-2 block ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
                        Messaggio *
                      </Label>
                      <Textarea
                        id="c-msg"
                        data-testid="contact-form-messaggio"
                        value={formData.messaggio}
                        onChange={(e) => handleChange("messaggio", e.target.value)}
                        placeholder="Come possiamo aiutarvi?"
                        rows={5}
                        className={`border rounded-none resize-none ${isDark ? "bg-[#0A0A0A] border-white/20 text-white placeholder:text-white/30" : "bg-[#F5F0E8] border-black/10 text-[#1A1A1A] placeholder:text-black/30"} focus:border-[#C9A646] focus:ring-1 focus:ring-[#C9A646]`}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-form-submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        "Invio in corso..."
                      ) : (
                        <>
                          <Send className="w-4 h-4" strokeWidth={1.5} />
                          Invia Messaggio
                        </>
                      )}
                    </button>
                  </form>
                )}
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section data-testid="map-section" className={`py-20 md:py-24 transition-colors duration-500 ${isDark ? "bg-[#0E0E0E]" : "bg-[#EDE8DC]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-4">
              Dove Siamo
            </span>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal mb-10 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
              La nostra
              <span className="italic text-[#C9A646]"> posizione</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="border border-white/5 overflow-hidden" style={{ height: "400px" }}>
              <iframe
                title="Transfer Avellino Location"
                data-testid="google-map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3025.1!2d14.7715835!3d40.9146076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x133bcd0078462257%3A0x919a312e8ee3ad67!2sTransfer%20Avellino!5e0!3m2!1sit!2sit!4v1700000000000!5m2!1sit!2sit"
                width="100%"
                height="100%"
                style={{ border: 0, filter: isDark ? "invert(90%) hue-rotate(180deg) brightness(0.95) contrast(0.9)" : "none" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
